
import { defineComponent } from "vue";
import { DatasetType } from "@/types/dataset";
import { CancelTokenSource } from "axios";
import { useApiStore } from "@/stores/apiStore";
import { FormatBytes } from "@/utils/Utilities";
import type { TableData } from "@/components/additional_components/table.vue";
import i18n from "@/utils/i18n";
type ICoreServicesLogData = {
	PartitionKey: string;
	RowKey: string;
	datasetId: string;
	filePath: string;
	message: string;
	logType: string;
	logId: string;
	logClass: string;
	utc: string;
};
export type ICoreServicesClientLogs = Pick<
	ICoreServicesLogData,
	"datasetId" | "filePath" | "message" | "logType" | "utc"
>;
export interface IListDatasetLogsResponseDTO {
	id: string;
	datasetSize: number;
	createdAt: Date;
	createdBy: { name: string };
	updatedAt?: Date;
	updatedBy?: { name: string };
	logs: ICoreServicesClientLogs[];
}
export default defineComponent({
	name: "imdex-dataset-properties-and-logs",
	props: {
		id: {
			type: String,
			default: ""
		},
		name: {
			type: String,
			default: ""
		},
		datasetType: { type: String, default: "Drillhole" as DatasetType },
		group: { type: String, default: "" },
		status: {
			type: String,
			default: ""
		},
		handleCloseCallback: {
			type: Function,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			default: () => {}
		}
	},
	setup() {
		const formatBytes = FormatBytes;
		const api = useApiStore();
		return { api, formatBytes };
	},
	data() {
		return {
			tab: 0,
			loading: true,
			errorFetchingLogs: false,
			source: null as null | CancelTokenSource,
			properties: {
				datasetSize: 0,
				createdBy: "",
				createdAt: new Date(),
				updatedBy: "",
				updatedAt: new Date()
			} as {
				datasetSize: number;
				createdBy: string;
				createdAt: Date;
				updatedBy?: string;
				updatedAt?: Date;
			},
			logsTableData: {
				columns: ["date_&_time", "type", "file_path", "message"],
				tableData: [],
				options: {
					thead: {
						background: "#E6E8E9",
						"font-size": "12px"
					},
					rows: {
						spacing: "10px"
					},
					columns: {
						"date_&_time": {
							sortable: true,
							minWidth: "20%", // attempting to force it have a single line for the date
							formatFn: this.formatDateTime as (date: Date) => string
						},
						type: {
							sortable: true,
							maxWidth: "5%"
						},
						file_path: {
							sortable: true,
							maxWidth: "25%"
						},
						message: {
							sortable: true,
							maxWidth: "25%"
						}
					}
				}
			} as TableData
		};
	},
	beforeMount() {
		this.loading = true;
		this.api
			.get(`dataset-logs?datasetId=` + this.id)
			.then(({ data }: { data: IListDatasetLogsResponseDTO }) => {
				const { datasetSize, createdAt, updatedAt } = data;
				const createdBy = data?.createdBy?.name ?? "";
				const updatedBy = data?.updatedBy?.name ?? "";
				this.properties = {
					datasetSize,
					createdBy,
					createdAt,
					updatedBy,
					updatedAt
				};
				this.logsTableData.tableData = data.logs.map((log) => {
					const date = log.utc.includes("T")
						? new Date(log.utc)
						: new Date(`${log.utc}Z`); // can be removed, supports logs from before my change to core-services by casting the timezone as utc
					return {
						"date_&_time": date,
						type: log.logType,
						file_path: log.filePath,
						message: log.message
					};
				});
				this.loading = false;
			})
			.catch(() => {
				this.errorFetchingLogs = true;
				this.loading = false;
			});
	},
	beforeUnmount() {
		this.api.cancel();
	},
	methods: {
		formatDateTime(date: Date) {
			// Format the time
			let hours = date.getHours();
			const minutes = String(date.getMinutes()).padStart(2, "0");
			const ampm =
				hours >= 12
					? this.$t("ssa.hierarchy.datasetPropertiesAndLogs.pm")
					: this.$t("ssa.hierarchy.datasetPropertiesAndLogs.am");
			hours = hours % 12;
			hours = hours ? hours : 12; // the hour '0' should be '12'
			const time = `${hours}:${minutes} ${ampm}`;

			// Format the date
			const day = String(date.getDate()).padStart(2, "0");
			const month = date.toLocaleString(i18n.global.locale, { month: "short" });
			const year = date.getFullYear();
			const formattedDate = `${day}-${month}-${year}`;

			return `${time}, ${formattedDate}`;
		}
	}
});
